import React from 'react';
import PropTypes from 'prop-types';

const NAMESPACE = 'grecaptcha';

let globalIdCount = 0;

export default class RecaptchaCore extends React.Component {
    static propTypes = {
        recaptchaGoogleCloudEnabled: PropTypes.bool.isRequired,
        sitekey: PropTypes.string.isRequired,
        /** Callback function executed when the user submits a successful response. */
        onVerify: PropTypes.func.isRequired,
        onLoad: PropTypes.func,
        /** Callback function executed when reCAPTCHA encounters an error (usually network
         * connectivity) and cannot continue until connectivity is restored. If you specify a
         * function here, you are responsible for informing the user that they should retry. */
        onError: PropTypes.func,
        /** The name of your callback function, executed when the reCAPTCHA response expires and
         * the user needs to re-verify. */
        onExpired: PropTypes.func,
        /** Used to reactively trigger a widgetId reset, clearing the response it had before */
        reset: PropTypes.bool,
        /** Function that returns window. Only for unit testing to mock it. */
        _getWindow: PropTypes.func,
    };
    static defaultProps = {
        onLoad: undefined,
        onError: undefined,
        onExpired: undefined,
        reset: undefined,
        _getWindow: () => window,
    };

    constructor(props) {
        super(props);

        this.window = this.props._getWindow();

        this.state = {
            ready: this._isGrecaptchaReady(),
            elementId: `g-recaptcha-${globalIdCount}`,
        };
        globalIdCount += 1;

        if (!this.state.ready) {
            this.readyCheck = setInterval(this._updateReadyState, 1000);
        }
    }

    componentDidMount() {
        if (this.state.ready) {
            this._renderGrecaptcha();
        }
    }

    componentWillUnmount() {
        clearInterval(this.readyCheck);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.ready && !prevState.ready) {
            this._renderGrecaptcha();
        }

        if (
            !prevProps.reset &&
            this.props.reset &&
            Number.isInteger(this.widgetId) &&
            this.widgetId >= 0 &&
            this._getGrecaptchaApi() &&
            this._getGrecaptchaApi().reset
        ) {
            this._getGrecaptchaApi().reset(this.widgetId);
        }
    }

    _isGrecaptchaReady() {
        return (
            typeof this.window !== 'undefined' &&
            typeof this._getGrecaptchaApi() !== 'undefined' &&
            typeof this._getGrecaptchaApi().render === 'function'
        );
    }

    _updateReadyState = () => {
        if (this._isGrecaptchaReady()) {
            this.setState({ ready: true });

            clearInterval(this.readyCheck);
        }
    };

    _verifyCallback = (token) => {
        const tokenToSend = this.props.recaptchaGoogleCloudEnabled
            ? JSON.stringify({ token, version: 'enterprise' })
            : token;
        if (this.props.onVerify) {
            this.props.onVerify(tokenToSend);
        }
    };

    _getGrecaptchaApi() {
        if (this.props.recaptchaGoogleCloudEnabled) {
            return this.window[NAMESPACE]?.enterprise;
        }
        return this.window[NAMESPACE];
    }

    _renderGrecaptcha() {
        this.widgetId = this._getGrecaptchaApi().render(this.state.elementId, {
            sitekey: this.props.sitekey,
            callback: this._verifyCallback,
            'error-callback': this.props.onError,
            'expired-callback': this.props.onExpired,
        });

        if (this.props.onLoad) {
            this.props.onLoad();
        }
    }

    render() {
        return <div id={this.state.elementId} />;
    }
}
